import React, { useState } from "react";
import { FiCheck, FiSave } from "react-icons/fi";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import Step1SectionMeta from "./Step1SectionMeta";
import Step2QuestionsForm from "./Step2QuestionsForm";
import { ToastContainer } from "react-toastify";

export default function AddSectionForm({ examId, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [sectionData, setSectionData] = useState({
    sourceId: examId,
    sectionName: "",
    totalQuestions: "",
    paragraph: {
      isParagraph: false,
      text: "",
    },
    questions: [],
  });

  const handleAddQuestion = () => {
    setSectionData((prev) => {
      if (prev.questions.length >= prev.totalQuestions) {
        showErrorToast(`You can add only ${prev.totalQuestions} questions`);
        return prev;
      }

      return {
        ...prev,
        questions: [
          ...prev.questions,
          {
            questionNo: prev.questions.length + 1,
            questionType: "text",
            questionText: "",
            options: { A: "", B: "", C: "", D: "" },
            correctOption: "",
          },
        ],
      };
    });
  };

  const handleRemoveQuestion = (index) => {
    setSectionData((prev) => {
      const questions = prev.questions
        .filter((_, i) => i !== index)
        .map((q, i) => ({ ...q, questionNo: i + 1 }));

      return {
        ...prev,
        questions,
        totalQuestions: questions.length,
      };
    });
  };

  const handleProceedToQuestions = () => {
    if (!sectionData.sectionName?.trim()) {
      return showErrorToast("Section name is required");
    }

    if (!sectionData.totalQuestions || sectionData.totalQuestions < 1) {
      return showErrorToast("Total questions must be at least 1");
    }

    if (
      sectionData.paragraph.isParagraph &&
      !sectionData.paragraph.text?.trim()
    ) {
      return showErrorToast("Paragraph text is required");
    }

    setSectionData((prev) => ({
      ...prev,
      questions: [
        {
          questionNo: 1,
          questionType: "text",
          questionText: "",
          optionsType: "text",
          options: { A: "", B: "", C: "", D: "" },
          correctOption: "",
        },
      ],
    }));
    setStep(2);
  };

  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...sectionData.questions];
    if (field.includes("options.")) {
      const optKey = field.split(".")[1];
      updatedQuestions[index].options[optKey] = value;
    } else {
      updatedQuestions[index][field] = value;
    }
    setSectionData((prev) => ({ ...prev, questions: updatedQuestions }));
  };

  const validateQuestions = (questions) => {
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.questionText?.trim()) {
        showErrorToast(`Question ${i + 1} is required`);
        return false;
      }
      for (const key of ["A", "B", "C", "D"]) {
        if (!q.options[key]?.trim()) {
          showErrorToast(`Option ${key} is required in Question ${i + 1}`);
          return false;
        }
      }
      if (!["A", "B", "C", "D"].includes(q.correctOption)) {
        showErrorToast(`Correct option missing in Question ${i + 1}`);
        return false;
      }
    }
    return true;
  };

  const handleCreateNewSection = async () => {
    if (!sectionData.sectionName?.trim()) {
      return showErrorToast("Section name is required");
    }
    if (!validateQuestions(sectionData.questions)) return;

    try {
      setLoading(true);
      const payload = {
        sourceId: examId,
        sourceType: "Exam",
        sectionName: sectionData.sectionName.trim(),
        totalQuestions: sectionData.totalQuestions,
        paragraph: {
          isParagraph: sectionData.paragraph.isParagraph,
          text: sectionData.paragraph.text,
        },

        questions: sectionData.questions.map((q, i) => ({
          questionNo: i + 1,
          questionText: q.questionText.trim(),
          questionType: q.questionType || "text",
          optionsType: q.optionsType || "text",
          options: {
            A: q.options.A.trim(),
            B: q.options.B.trim(),
            C: q.options.C.trim(),
            D: q.options.D.trim(),
          },
          correctOption: q.correctOption,
        })),
      };

      const res = await makeRequestAuth("post", "/admin/addQuestion", payload);

      if (res?.error) {
        showErrorToast(res.message);
      } else {
        showSuccessToast("Section & Questions added successfully!");
        onComplete?.();
      }
    } catch (err) {
      showErrorToast(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <ToastContainer />
      <div className="bg-gray-50/80 px-6 py-5 flex flex-col sm:flex-row items-center justify-between border-b gap-4">
        <div className="flex items-center gap-3 md:gap-6">
          <StepBadge
            num={1}
            active={step >= 1}
            completed={step > 1}
            label="Section Info"
          />
          <div
            className={`hidden sm:block h-[2px] w-10 md:w-16 ${step === 2 ? "bg-blue-500" : "bg-gray-200"}`}
          />
          <StepBadge
            num={2}
            active={step === 2}
            completed={false}
            label="Configure Questions"
          />
        </div>
        <div className="hidden md:block">
          <span className="text-[10px] font-black text-gray-400 mr-10 uppercase tracking-[0.2em]">
            New Section Wizard
          </span>
        </div>
      </div>

      <div className="p-4 md:p-8 flex-1">
        {step === 1 ? (
          <Step1SectionMeta
            sectionData={sectionData}
            setSectionData={setSectionData}
            onNext={handleProceedToQuestions}
          />
        ) : (
          <Step2QuestionsForm
            questions={sectionData.questions}
            onQuestionChange={handleQuestionChange}
            onAddQuestion={handleAddQuestion}
            onRemoveQuestion={handleRemoveQuestion}
            onBack={() => setStep(1)}
            onSave={handleCreateNewSection}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
}

function StepBadge({ num, active, completed, label }) {
  return (
    <div className="flex items-center gap-2 md:gap-3">
      <div
        className={`w-7 h-7 md:w-9 md:h-9 rounded-xl flex items-center justify-center font-bold text-xs md:text-sm transition-all duration-300 ${
          active
            ? "bg-blue-600 text-white shadow-lg shadow-blue-100 rotate-[360deg]"
            : "bg-gray-200 text-gray-400"
        }`}
      >
        {completed ? <FiCheck size={16} /> : num}
      </div>
      <span
        className={`text-xs md:text-sm font-bold transition-colors ${active ? "text-gray-800" : "text-gray-400"}`}
      >
        {label}
      </span>
    </div>
  );
}
